import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import moment from 'moment'
import GoogleMapsApiLoader from 'google-maps-api-loader'

Vue.use(Vuex)

export default new Vuex.Store({
	state: {
		// States
		loading: false,
		globalLanguage: null,
		markerWindow: null,
		sideMenuOpen: false,
		sideProfileOpen: false,
		filterOpen: true,
		filterHeight: null,
		prestepOpen: false,
		coachOpen: false,
		duration: null,
		distance: null,
		mapPreserveViewport: true,
		originPoint: null,
		destinationPoint: null,
		remindChallenge: false,
		errorMessage: null,
		walkingCircles: null,
		coachEffect: null,
		surveyId: null,
		parsedCalculatorShortcodes: {},
		calculatorRerender: 'key',
		surveySent: false,

		// States from intro
		transportPreference: null,
		toFromIntro: null,
		fromFromIntro: null,
		hasChangedTo: null,

		directions: {
			from: null,
			to: null,
			travelMode: 'BICYCLING',
		},

		/* @type HTMLMarker[] */
		markers: [],
		markerClusterer: null,

		// Data
		points: [],
		layers: [],
		endUser: {},
		client: {},
		translations: {},
		answers: {},

		// APIs
		mapsApi: null
	},

	getters: {
		translatedLayers: state => {
			return state.layers.map(l => {
				let match = l.translations?.find(t => t.layout === 'translation' && t.attributes.lang === state.endUser.language)

				l.__title = match ? match.attributes.title : l.title

				return l
			})
		},

		translations: state => {
			return state.translations
		},

		cards: state => {
			return state.layers.map(l => l.cards).flat()
				.filter(c => ! c.lang || c.lang === state.endUser.language)
		},

		shortcodes: state => {
			let shortcodes = state.endUser.tags?.filter(t => t.shortcodes).reduce((array, t) => {
				return array.concat(t.shortcodes.map(s => {
					return {shortcode: s.attributes.shortcode, value: s.attributes.value, precedence_score: s.attributes.precedence_score, tag_id: t.id}
				}))
			}, [])

			// Filter based on precedence score
			let filtered = []

			shortcodes?.forEach(shortcode => {
				let existing = filtered.find(s => s.shortcode === shortcode.shortcode)

				if (existing) {
					if (existing?.precedence_score === null || parseInt(shortcode?.precedence_score || 0, 10) > parseInt(existing?.precedence_score || 0, 10)) {
						filtered = filtered.filter(x => x != existing)
						filtered.push(shortcode)
					}
				} else {
					filtered.push(shortcode)
				}
			})

			return filtered
		}
	},

	mutations: {
		async loadGoogleMaps(state) {
			await loadGoogleMaps(state)
		},

    setSurveyResponses(state, responses) {
      state.surveyResponses = responses;
    },

		async setup(state, payload) {
			// Special for test (activate all layers)
			if (payload.endUser && payload.endUser.tags.find(t => t.id == 87)) {
				payload.layers = payload.layers.map(l => {
					l.show_as_default = true
					return l
				})
			}

			// Determine which layers and point categories should be active per default
			// Layers default to active=true; parseRules() will deactivate layers whose rules don't match
			// show_as_default is only used for point_categories (map markers), not for carousel/prestep flow
			payload.layers = payload.layers.map(l => {
				l.active = true // Default to active; rules will deactivate if needed

				l.point_categories.forEach(c => {
					c.active = l.show_as_default || c.show_as_default
				})

				return l
			})

			state.layers = payload.layers
			state.points = payload.points
			state.translations = payload.translations

			if (payload.endUser) {
				state.endUser = payload.endUser
				state.client = payload.endUser.client

				// Fallback to client language, if there's none on the end user
				if ( ! state.endUser.language) {
					state.endUser.language = state.client.language_slug
				}
			} else {
				state.client = payload.client
				state.endUser = null
				state.destinationPoint = payload.destination
			}

			// Client loaded — Load Google Maps if consent
			let consent = state.client?.google_maps_consent || true

			if (
				(
					consent === true
					|| consent === 'on'
					|| consent === 'consent' && state.endUser.googleMapsConsent
				)
				&& state.client?.no_map !== 1
			) {
				await loadGoogleMaps(state)
			}

			// If there's an address on the end user, set make destination center to false
			if (state.endUser && state.endUser.address && ( ! state.client || ! state.client.make_destination_center)) {
				state.client.make_destination_center = false
			}

			if (state.client && ! state.client.make_destination_center && state.endUser && ! state.endUser.address && ! state.endUser.origin) {
				state.client.make_destination_center = true
			}

			// Set title
			document.title = state.client.title_prefix ? state.client.title_prefix + ' ' + state.client.title : state.client.title

			// Setup map and directions
			if (payload.endUser) {
				if ( ! state.client.no_map) {
					state.directions.travelMode = payload.defaultTravelMode

					if (state.endUser.origin) {
						if (state.endUser.origin.address && state.endUser.origin.address.trim()) {
							state.directions.from = state.endUser.origin.address.trim() + ', ' + state.endUser.origin.city
						} else {
							state.directions.from = state.endUser.origin.title + ', ' + state.endUser.origin.city
						}

						state.originPoint = state.endUser.origin
					}

					if (state.toFromIntro) {
						state.client.make_destination_center = false
					} else {
						if (state.endUser.destination && state.endUser.destination.address && state.endUser.destination.address.trim()) {
							state.directions.to = state.endUser.destination.address + ', ' + state.endUser.destination.city
						}

						if (state.endUser.destination) {
							state.destinationPoint = state.endUser.destination
						}

						// Make point special if it's destination and special formatting is on
						if (state.client.default_destination_special_formatting) {
							let destinationPoint = state.points.find(p => p.id == state.destinationPoint.id)

							if (destinationPoint) {
								destinationPoint.special = true
							}
						}
					}
				}

				determineFinished(state)

				// If custom CSS, inject
				if (state.client?.custom_css) {
					document.head.insertAdjacentHTML('beforeend', '<style>' + state.client.custom_css + '</style>')
				}
			}

			// Change destination if previously entered destination is present in cookie
			// Also, if it's not a test user
			//if (JsCookies.get('toDestination') && ! payload.endUser?.test_user) {
			if (this._vm.$getCookie('toDestination')) {
				state.directions.to = this._vm.$getCookie('toDestination')
				state.toFromIntro = true
			}

			if (this._vm.$getCookie('coachDestination') && ! payload.endUser?.test_user) {
				state.directions.to = this._vm.$getCookie('coachDestination')
				state.toFromIntro = true
			}

			if (this._vm.$getCookie('fromDestination')) {
				state.directions.from = this._vm.$getCookie('fromDestination')
				state.fromFromIntro = true
			}

			// E.g. determine hidden
			parseRules(state)

			// Go through all the cards to determine featured cards
			let maxFeaturedCards = state.endUser.client.customization?.max_featured_cards || 5

			let cards = state.layers
				.map(l => l.cards)
				.flat()
				.filter(c => c.featuredScore2 && ! c.hidden)
				.sort((b, a) => a.featuredScore2 - b.featuredScore2)
				.slice(0, maxFeaturedCards)
				.forEach(c => {
					c.isFeatured = true
				})

			// Setup transport preferences (from intro)
			if (state.transportPreference) {
				state.directions.travelMode = {'Kollektivtrafik': 'TRANSIT', 'Promenad': 'WALKING', 'Bil': 'DRIVING', 'Cykel': 'BICYCLING'}[state.transportPreference] || state.directions.travelMode
				state.layers.find(l => l.title.includes(state.transportPreference)).active = true
			}

			// Add GTM-script if applicable
			if ( ! state.client.no_analytics) {
				let el = document.createElement('script')
				el.text = "(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0], j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src= 'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f); })(window,document,'script','dataLayer','GTM-NGWC64J');"
				document.head.appendChild(el)
			}

			// GTM
			if (state.endUser) {
				let borough = state.endUser.tags?.find(t => t.title?.includes('stadsdelsområden'))

				this._vm.$metric({
					'event': 'dataLayer_ready',
					'PageClient': state.client.title,
					'PageClientGeo': borough ? borough.title : null,
					'PageEndUserTags': state.endUser.tags.map(t => t.title).join(', '),
					'PageVersion': '2'
				 })
			}

			// Heartbeat zero
			axios.post(this._vm.$apiUrl + '/api/heartbeat', {slug: state.endUser.slug, seconds: 0})

			state.loading = false

			// Set temporary language on the end user if provided in the query
			const urlParams = new URLSearchParams(window.location.search)
			const lang = urlParams.get('lang')

			if (lang) {
				//state.endUser.language = lang
				this._vm.$changeLang(lang)
			}
		},

		registerBehavior(state, behavior) {
    		axios.post(this._vm.$apiUrl + '/api/behavior', behavior).then(res => {
				state.endUser.behaviors.push(res.data)
				state.endUser.behaviors.sort((a, b) => a.created_at > b.created_at ? 1 : -1)
			})
		},

		updateLayers(state, layers) {
			// Preserve active states when updating layers
			const activeStates = state.layers.reduce((acc, layer) => {
				acc[layer.id] = {
					active: layer.active,
					point_categories: layer.point_categories.map(cat => ({
						id: cat.id,
						active: cat.active
					}))
				}
				return acc
			}, {})

			// Update layers while preserving active states
			state.layers = layers.map(layer => {
				const activeState = activeStates[layer.id]
				if (activeState) {
					layer.active = activeState.active
					layer.point_categories = layer.point_categories.map(cat => {
						const activeCat = activeState.point_categories.find(ac => ac.id === cat.id)
						if (activeCat) {
							cat.active = activeCat.active
						}
						return cat
					})
				}
				return layer
			})
		}
	}
})

async function loadGoogleMaps(state) {
	await GoogleMapsApiLoader({
		libraries: ['places'],
		apiKey: 'AIzaSyAcIZMBpu2BWTabhJPCRGnXwx9ahS5DGAw'
	}).then((mapsApi) => {
		state.mapsApi = mapsApi
	})
}

function parseRules(state) {
	let userTagIds = state.endUser ? state.endUser.tags.map(t => t.id) : []
	let userEvents = state.endUser?.events || []

	// Helper function to evaluate a single condition
	function evaluateCondition(condition, userTagIds, userEvents) {
		if (!condition.attributes) {
			return false
		}

		if (condition.layout === 'event_condition') {
			const eventKey = condition.attributes.event
			const eventValue = condition.attributes.value
			const not = condition.attributes.not || false

			// Check if user has an event matching the condition
			const hasEvent = eventKey && eventValue && userEvents.some(e => {
				return e.event_type_id && e.value &&
				  `${e.event_type_id}` === `${eventKey}` &&
				  `${e.value}` === `${eventValue}`
			})

			return not ? !hasEvent : hasEvent
		} else if (condition.attributes.tag) {
			const tagId = parseInt(condition.attributes.tag, 10)
			if (Number.isNaN(tagId)) {
				return false
			}
			return userTagIds.includes(tagId)
		}

		return false
	}

	// Helper function to evaluate all conditions in a rule
	function evaluateRule(rule, userTagIds, userEvents) {
		if (!rule.conditions || rule.conditions.length === 0) {
			return false
		}
		// All conditions must match for the rule to be true
		return rule.conditions.every(condition => evaluateCondition(condition, userTagIds, userEvents))
	}

	state.layers.forEach((layer) => {
		// Evaluate card rules
		layer.cards.forEach((card) => {
			if (card.rules && card.rules.length > 0) {
				card.hidden = true
				const oneRuleTrue = card.rules.some(rule => evaluateRule(rule, userTagIds, userEvents))
				if (oneRuleTrue) {
					card.hidden = false
				}
			}
		})

		// Evaluate layer rules
		if (layer.rules && layer.rules.length > 0) {
			const onlyShowIfRules = layer.rules.filter(rule => rule.effect === 'only_show_if')
			const otherRules = layer.rules.filter(rule => rule.effect !== 'only_show_if')

			if (onlyShowIfRules.length > 0) {
				// For only_show_if rules, ALL must match (restrictive)
				const allOnlyShowIfMatch = onlyShowIfRules.every(rule => evaluateRule(rule, userTagIds, userEvents))

				// If there are other rules, at least one must match (permissive)
				let oneOtherRuleMatches = true
				if (otherRules.length > 0) {
					oneOtherRuleMatches = otherRules.some(rule => evaluateRule(rule, userTagIds, userEvents))
				}

				// Layer is active only if all only_show_if rules match AND (no other rules OR at least one other rule matches)
				if (allOnlyShowIfMatch && oneOtherRuleMatches) {
					layer.active = true
					layer.point_categories.forEach(c => { c.active = true })
				} else {
					layer.active = false
					layer.point_categories.forEach(c => { c.active = false })
				}
			} else {
				// Original behavior for non-only_show_if rules: activate if at least one matches
				const oneRuleTrue = layer.rules.some(rule => evaluateRule(rule, userTagIds, userEvents))
				if (oneRuleTrue) {
					layer.active = true
					layer.point_categories.forEach(c => { c.active = true })
				}
			}
		}
	})
}

function determineFinished(state) {
	// Also run a quick check on the cards
	state.layers.forEach(layer => {
		layer.cards = layer.cards.map(card => {
			let finished = state.endUser.behaviors.find(b => b.card_id == card.id && b.title === 'Finished')

			if (finished) {
				card.finished = true
			}

			return card
		})
	})
}
